import { useState, useEffect, useContext } from "react";
import {
  Layout,
  Row,
  Col,
  Form,
  Input,
  Button,
  Spin,
  Typography,
  Divider,
  List,
  Modal,
  Tag,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  UnlockFilled,
} from "@ant-design/icons";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import {
  GetReEditPasswordList,
  AddEditReEditPassword,
  GetSpecificReEditPassword,
} from "../../../../utils/api.constant";
import { eResultCode } from "../../../../utils/enum";
import styles from "./password.module.css";
import DateUtils from "../../../../utils/date";
import { TContext, TResponseModel } from "../../../../types/config";
import { UserContext } from "../../../../context";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import EncryptUtils from "../../../../utils/encrypt";

const { Paragraph } = Typography;

type TPasswordEntry = {
  id: number;
  createdOn: string;
  createdBy: number;
  createdByName: string;
  isDeleted: number;
  isActive: number;
  password?: string;
  remark?: string;
};

const PasswordManager = () => {
  const [form] = Form.useForm();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [passwordList, setPasswordList] = useState<TPasswordEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPassword, setSelectedPassword] =
    useState<TPasswordEntry | null>(null);
  const context: TContext = useContext(UserContext);

  const fetchPasswordList = async () => {
    setIsLoading(true);
    try {
      const response: TResponseModel = await post(GetReEditPasswordList, {
        data: {
          currentPage: 1,
          PageSize: -1,
        },
      });

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        setPasswordList(response.data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error(error);
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Exception while reteriving the password logs",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPasswordList();
  }, []);

  const onFinish = async (values: { password?: string; remark?: string }) => {
    try {
      const response: TResponseModel = await post(AddEditReEditPassword, {
        data: {
          password: EncryptUtils.encrypt(values?.password as string),
          remark: values.remark,
        },
      });

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        form.resetFields();
        fetchPasswordList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error(error);
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "An error occurred.",
      });
    }
  };

  const handleUnlock = async (id: number) => {
    try {
      const response: TResponseModel = await post(GetSpecificReEditPassword, {
        data: { id },
      });

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        setSelectedPassword(response.data);
        setIsModalVisible(true);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error(error);
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "An error has occured",
      });
    }
  };

  return (
    <Layout className={styles.passwordManagerLayout}>
      <Row
        gutter={[16, 16]}
        style={{
          padding: "1rem",
        }}
      >
        {" "}
        <Col
          xs={{ span: 24, order: 2 }}
          sm={{ span: 24, order: 2 }}
          md={{ span: 12, order: 1 }}
          lg={{ span: 12, order: 1 }}
          xl={{ span: 12, order: 1 }}
        >
          {" "}
          <h3>{passwordList.length} Total Logs </h3>
          <Spin spinning={isLoading}>
            <div
              style={{ maxHeight: "75vh", overflowY: "auto", paddingRight: 8 }}
            >
              <List
                className={styles.passwordList}
                itemLayout="horizontal"
                dataSource={passwordList}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        icon={<UnlockFilled />}
                        onClick={() => handleUnlock(item.id)}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <>
                          <label
                            style={{ color: "var(--bs-dark)" }}
                          >{`Created by ${item.createdByName}`}</label>
                          <Tag
                            color={item.isActive ? "green" : "red"}
                            style={{ marginLeft: 8 }}
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Tag>
                        </>
                      }
                      description={`On ${DateUtils.format(item.createdOn, context?.user?.dateFormat, true)}`}
                    />
                  </List.Item>
                )}
              />
            </div>
          </Spin>
        </Col>
        <Col
          xs={{ span: 0, order: 2 }}
          sm={{ span: 0, order: 2 }}
          md={{ span: 1, order: 2 }}
          lg={{ span: 1, order: 2 }}
          xl={{ span: 1, order: 2 }}
        >
          <Divider type="vertical" className={styles.divider} />
        </Col>
        <Col
          xs={{ span: 24, order: 1 }}
          sm={{ span: 24, order: 1 }}
          md={{ span: 11, order: 2 }}
          lg={{ span: 11, order: 2 }}
          xl={{ span: 11, order: 2 }}
        >
          <h3>Create New Password for Reediting Eval-L1 and Eval -L2</h3>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ marginTop: "1rem" }}
          >
            <Form.Item
              name="password"
              label="Password"
              rules={[
                {
                  required: true,
                  message: "Please input your password!",
                },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/,
                  message:
                    "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item name="remark" label="Remark">
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Modal
        title="Password Details"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        {selectedPassword && (
          <>
            <Paragraph>
              <strong>Password:</strong>{" "}
              {EncryptUtils.decrypt(selectedPassword?.password as string)}
            </Paragraph>
            <Paragraph>
              <strong>Remark:</strong> {selectedPassword.remark}
            </Paragraph>
          </>
        )}
      </Modal>
    </Layout>
  );
};

export default PasswordManager;
